import { makeAutoObservable } from 'mobx'
import { debounce } from 'lodash'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IParamsCreateMessage } from 'entities/Message'
import { Conversation, type IParamsConversationCreate } from 'entities/Conversation'
import { ConversationGroupError } from 'entities/Conversation/model/ConversationGroupError'
import { ConversationMessagesStore } from 'widgets/ConversationMessages'
import { SendFromStore } from 'widgets/SendFrom'
import type { IScheduledData } from 'widgets/ConversationSchedule'
import { type ProfilePageStore } from 'pages/conversations/pages/search/pages/profile/store/profilePageStore'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'

export class SectionMessagesStore {
  private _loadingOpenConversation = false
  readonly conversationMessagesStore: ConversationMessagesStore
  readonly sendFromStore: SendFromStore
  private _initializedSendFrom = false
  debounceSendEventChangeInbox: ReturnType<typeof debounce>

  constructor(private _profilePageStore: ProfilePageStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this.debounceSendEventChangeInbox = debounce(this._sendEventChangeInbox, 500)
    this.conversationMessagesStore = new ConversationMessagesStore(this._entities)
    this.sendFromStore = new SendFromStore(this._entities, {
      autoSelectInbox: true,
      personalInboxesOnly: true,
      includeRCS: true,
      onChangeInboxId: (id) => {
        if (!id) return

        this.conversationMessagesStore.setInboxId(id)
      },
      getDropdownItemsOptions: {
        showOnlyVerifiedToolFree: true,
      },
    })

    this.conversationMessagesStore.setConfig({
      onStartSendProcess: (message, scheduledData, conversationId, withUndo, onSendSuccess) => {
        this.sendFromStore.checkError()
        this._onConversationCreate(scheduledData, false, message, withUndo, onSendSuccess)
      },
      eventSegmentSendType: 'Search page',
    })
  }

  private _sendEventChangeInbox = () => {
    if (!this._initializedSendFrom) {
      if (this.sendFromStore.inbox_id) {
        logV2SearchEvent(
          'BigConvSlideout_Inbox_Selector_Set',
          {
            event_id: 'bigconvslideout_inbox_selector_set',
            type: 'Default Inbox Appeared',
          },
          this._entities
        )
      } else {
        logV2SearchEvent(
          'BigConvSlideout_Inbox_Selector_Set',
          {
            event_id: 'bigconvslideout_inbox_selector_set',
            type: 'Empty State',
          },
          this._entities
        )
      }

      this._initializedSendFrom = true
    } else {
      logV2SearchEvent(
        'BigConvSlideout_Inbox_Selector_Set',
        {
          event_id: 'bigconvslideout_inbox_selector_set',
          type: 'Inbox Changed',
        },
        this._entities
      )
    }
  }

  private _onConversationCreate = async (
    scheduledData?: IScheduledData,
    isDoubleOptInButton?: boolean,
    message?: IParamsCreateMessage,
    withUndo?: boolean,
    onSendSuccess?: () => void
  ) => {
    if (!this.sendFromStore.inbox_id) return

    const messageFieldStore = this.conversationMessagesStore.messageFieldStore

    try {
      messageFieldStore.setLoading(true)

      const params: IParamsConversationCreate = {
        contact_id: this.contact.id,
        participants: [],
        team_id: this.sendFromStore.inbox_id,
      }

      if (this.sendFromStore.number_id) {
        params.number_id = this.sendFromStore.number_id
      }

      if (this.sendFromStore.smart_option) {
        params.smart_option = this.sendFromStore.smart_option
      }

      let conversation: Conversation | null = null
      const data = await this._entities.conversationStore.createConversation(params)

      if (data instanceof Conversation) {
        conversation = data
      }

      if (data instanceof ConversationGroupError) {
        conversation = await data.showModal()
      }

      if (conversation) {
        if (!isDoubleOptInButton) {
          if (messageFieldStore.promiseAttachmentLoading) {
            await messageFieldStore.promiseAttachmentLoading
          }

          const mergedMessage = {
            ...messageFieldStore?.messageRequestData,
            ...message,
            ...scheduledData,
          }

          if (withUndo) {
            this.showUndoProcess(mergedMessage, conversation.id, onSendSuccess)
          } else {
            await this.conversationMessagesStore.sendMessage({
              message: mergedMessage,
              conversationId: conversation.id,
              onSendSuccess,
            })
          }
          await messageFieldStore.reset()
        }

        this.sectionConversationStore.filtersStore.setFilter('open')
        this.sectionConversationStore.addConversation(conversation.origin, true)
        this.sectionConversationStore.setCurrentItemId(conversation.id)
        this._profilePageStore.setHideFilter(false)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      messageFieldStore.setLoading(false)
    }
  }

  showUndoProcess = (
    message: IParamsCreateMessage,
    conversationId: number,
    onSendSuccess?: () => void
  ) => {
    const id = parseInt(nanoid().replace(/\D/g, ''), 10)
    const toastId = String(id)
    const timer = 5000

    const timeoutId = setTimeout(() => {
      this.conversationMessagesStore.sendMessage({
        message,
        conversationId,
        onSendSuccess,
      })

      toastStore.remove(toastId)
    }, timer)

    toastStore.add({
      type: 'info',
      id: toastId,
      title: 'Sending in',
      withCountdown: true,
      timer,
      action: {
        text: 'Undo',
        onAction: () => {
          toastStore.remove(toastId)
          clearTimeout(timeoutId)
        },
      },
      secondAction: {
        text: 'Instant send',
        onAction: () => {
          this.conversationMessagesStore.sendMessage({
            message,
            conversationId,
            onSendSuccess,
          })
          toastStore.remove(toastId)
          clearTimeout(timeoutId)
        },
      },
    })
  }

  setLoadingOpenConversation = (value: boolean) => {
    this._loadingOpenConversation = value
  }

  setInitializedSendFrom = (value: boolean) => {
    this._initializedSendFrom = value
  }

  openConversation = async () => {
    try {
      logV2SearchEvent(
        'BigConvSlideout_Open_In_Conv_Clicked',
        {
          event_id: 'bigconvslideout_open_in_conv_clicked',
        },
        this._entities
      )

      const inboxId = this._entities.inboxesStore.currentInboxId

      if (this.conversation) {
        this.setLoadingOpenConversation(true)

        if (inboxId !== this.conversation.inbox_id) {
          await this._entities.inboxesStore.handleUpdateTeamInbox(
            this.conversation.inbox_id,
            'fast'
          )
        }

        await this._entities.conversationStore.conversationOpenStore.setSignal({
          type: 'open_exist_conversation',
          payload: this.conversation.origin,
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this.setLoadingOpenConversation(false)
    }
  }

  loadMessages = () => {
    this.conversationMessagesStore.resetMessage()

    if (!this.sendFromStore.inbox_id) return

    this.conversationMessagesStore.loadMessagesContact({
      before: this.conversationMessagesStore.loadingBeforeId,
    })
  }

  init = () => {
    this.conversationMessagesStore.initReactions()
  }

  dispose = () => {
    this._loadingOpenConversation = false
    this.conversationMessagesStore.reset()
    this.conversationMessagesStore.resetReactions()
    this.conversationMessagesStore.resetConversationId()
    this.sendFromStore.reset()
    this.debounceSendEventChangeInbox?.cancel?.()
  }

  get sectionConversationStore() {
    return this._profilePageStore.sectionConversationStore
  }

  get conversation() {
    return this.sectionConversationStore.conversation
  }

  get inbox() {
    return this._entities.inboxesStore.getItem(this.conversation?.inbox_id)
  }

  get loadingOpenConversation() {
    return this._loadingOpenConversation
  }

  get contact() {
    return this._profilePageStore.contact
  }
}
